/**
 * External dependencies
 */
import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/table';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

/**
 * Internal dependencies
 */
import { Card, CardContent } from '@Components/Card';
import Breadcrumbs from '@Components/Breadcrumbs';
import Heading from '@Components/Heading';
import Loader from '@Components/Loader';
import service from '../../service';
import { usePhoneNumbers } from '@Hooks/usePhoneNumbers';
import { useUsers } from '@Hooks/useUsers';
import type { User } from '@Types/user';
import type { Inbox } from '@Types/inbox';
import type { PhoneNumberKind, PhoneNumberRow } from '@Types/phoneNumber';

const KINDS: PhoneNumberKind[] = ['DEDICATED', 'POOL'];

const SELECT_CLASS =
  'border-input focus-visible:border-ring focus-visible:ring-ring/50 flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:ring-[3px]';

function RegisterNumberDialog() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [providerId, setProviderId] = useState('');
  const [kind, setKind] = useState<PhoneNumberKind>('DEDICATED');
  const [region, setRegion] = useState('');

  const reset = () => {
    setPhoneNumber('');
    setProviderId('');
    setKind('DEDICATED');
    setRegion('');
  };

  const registerMutation = useMutation({
    mutationFn: () =>
      service.registerPhoneNumber({
        phoneNumber: phoneNumber.trim(),
        providerId: providerId.trim() || undefined,
        kind,
        region: region.trim() || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phone-numbers'] });
      toastSuccess('Number registered.');
      reset();
      setOpen(false);
    },
    onError: (e) =>
      toastError(e instanceof Error ? e.message : 'Failed to register number'),
  });

  const canSubmit =
    !registerMutation.isPending && phoneNumber.trim().length > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button>Register number</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Register number</DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) registerMutation.mutate();
          }}
        >
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-zinc-700">
              Phone number (E.164) *
            </label>
            <Input
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="e.g. +12015551234"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-zinc-700">
              Provider ID (optional)
            </label>
            <Input
              value={providerId}
              onChange={(e) => setProviderId(e.target.value)}
              placeholder="e.g. PNxxxxxxxxxxxxxxxx"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-zinc-700">Kind</label>
            <select
              className={SELECT_CLASS}
              value={kind}
              onChange={(e) => setKind(e.target.value as PhoneNumberKind)}
            >
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-zinc-700">
              Region (optional)
            </label>
            <Input
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              placeholder="e.g. US"
            />
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={!canSubmit}>
              {registerMutation.isPending ? 'Registering…' : 'Register'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AssignNumberDialog({ row }: { row: PhoneNumberRow }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState('');

  const { data: usersResponse } = useUsers({
    searchParams: { 'page[size]': 200 },
  });
  const users: User[] = usersResponse?.data ?? [];

  // Existing inboxes are needed to reuse a staffer's SMS inbox when one
  // already exists; otherwise a new SMS inbox is created for the user.
  const { data: inboxes } = useQuery<Inbox[]>({
    queryKey: ['inboxes', 'all'],
    queryFn: async () => service.readAllInboxes(),
    enabled: open,
  });

  const assignMutation = useMutation({
    mutationFn: async () => {
      const existing = (inboxes ?? []).find(
        (inbox) => inbox.userId === userId && inbox.inboxType === 'SMS',
      );
      let inboxId = existing?.id;
      if (!inboxId) {
        const created = await service.createInbox({
          inboxType: 'SMS',
          userId,
          phoneNumber: row.phoneNumber,
        });
        inboxId = created.id;
      }
      return service.assignPhoneNumber(row.id, inboxId!);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phone-numbers'] });
      queryClient.invalidateQueries({ queryKey: ['inboxes', 'all'] });
      queryClient.invalidateQueries({ queryKey: ['inbox-management'] });
      toastSuccess('Number assigned.');
      setUserId('');
      setOpen(false);
    },
    onError: (e) =>
      toastError(e instanceof Error ? e.message : 'Failed to assign number'),
  });

  const canSubmit = !assignMutation.isPending && userId.length > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setUserId('');
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Assign
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Assign {row.phoneNumber}</DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) assignMutation.mutate();
          }}
        >
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-zinc-700">
              Staff member
            </label>
            <select
              className={SELECT_CLASS}
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            >
              <option value="">Select a staff member…</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name || u.email || u.id}
                </option>
              ))}
            </select>
            <p className="text-xs text-zinc-500">
              Assigns this number to the staffer's SMS inbox, creating one if
              they don't have it yet.
            </p>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={!canSubmit}>
              {assignMutation.isPending ? 'Assigning…' : 'Assign'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UnassignButton({ row }: { row: PhoneNumberRow }) {
  const queryClient = useQueryClient();
  const unassignMutation = useMutation({
    mutationFn: () => service.unassignPhoneNumber(row.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phone-numbers'] });
      queryClient.invalidateQueries({ queryKey: ['inbox-management'] });
      toastSuccess('Number unassigned.');
    },
    onError: (e) =>
      toastError(e instanceof Error ? e.message : 'Failed to unassign number'),
  });

  return (
    <Button
      variant="outline"
      size="sm"
      disabled={unassignMutation.isPending}
      onClick={() => unassignMutation.mutate()}
    >
      {unassignMutation.isPending ? 'Unassigning…' : 'Unassign'}
    </Button>
  );
}

function assignedLabel(row: PhoneNumberRow): string {
  if (!row.inbox) return 'Unassigned';
  const user = row.inbox.user;
  return user?.name || user?.email || row.inbox.id;
}

export default function PhoneNumbers() {
  const { data: numbers, status } = usePhoneNumbers();
  const rows = numbers ?? [];

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'Admin', href: '/phone-numbers' },
          { name: 'Numbers', href: '/phone-numbers' },
        ]}
      />
      <div className="mt-8 mb-4 flex items-start justify-between gap-4">
        <div>
          <Heading>Numbers</Heading>
          <p className="text-sm text-zinc-500">
            Owned phone numbers — register a number and assign it to a staff SMS
            inbox.
          </p>
        </div>
        <RegisterNumberDialog />
      </div>
      <Card>
        <CardContent>
          {status === 'pending' ? (
            <Loader size="lg" />
          ) : !rows.length ? (
            <div className="py-8 text-center text-sm text-zinc-500">
              No numbers found.
            </div>
          ) : (
            <Table className="w-full">
              <TableHeader>
                <TableRow>
                  <TableHead>Number</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Assigned inbox</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">
                      {row.phoneNumber}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{row.kind}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          row.status === 'ACTIVE' ? 'default' : 'secondary'
                        }
                      >
                        {row.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{assignedLabel(row)}</TableCell>
                    <TableCell className="text-right">
                      {row.inboxId ? (
                        <UnassignButton row={row} />
                      ) : (
                        <AssignNumberDialog row={row} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
